import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import he from "he";
import "./editor.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import {
  deleteQuestion,
  exportQuestionsCSV,
  getAdminQuestions,
  importQuestionsCSV,
  saveQuestion,
} from "../../functions/getAdminQuestions";

const MAX_QUESTION_LEN = 350;
const MEDIA_ACCEPT = ".png,.jpg,.jpeg,.gif,.mp4,.mp3";
const ALLOWED_EXT = ["jpg", "jpeg", "png", "gif", "mp4", "mp3"];
const MAX_FILE_MB = 10;
const PAGE_SIZE = 10;
const OPTIONS = ["Yes", "Maybe", "No"];
const MAX_SCORE = 100;
const MAX_QUESTIONS = 100;

/** Page numbers with "..." gaps, e.g. 1 ... 4 5 6 ... 12 */
function pageItems(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) items.push("...");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("...");
  items.push(total);
  return items;
}

/** digits only, and never 0 (an empty box is shown instead) */
const cleanScore = (v) => {
  const digits = String(v).replace(/[^0-9]/g, "");
  return digits === "" || parseInt(digits, 10) < 1 ? "" : String(parseInt(digits, 10));
};

const mediaType = (name) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (ext === "mp4") return "video";
  if (ext === "mp3") return "audio";
  return "image";
};

function Media({ q }) {
  if (!q.hintUrl) return null;
  if (q.hintType === "video") {
    return (
      <video className="qe-media" controls>
        <source src={q.hintUrl} type="video/mp4" />
      </video>
    );
  }
  if (q.hintType === "audio") {
    return (
      <audio className="qe-media" controls>
        <source src={q.hintUrl} type="audio/mpeg" />
      </audio>
    );
  }
  return <img className="qe-media" src={q.hintUrl} alt="" />;
}

export default function Editor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // add / edit modal
  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', id, current }
  const [question, setQuestion] = useState("");
  const [scores, setScores] = useState(["", "", ""]);
  const [file, setFile] = useState(null);
  const [removeHint, setRemoveHint] = useState(false);
  const [filePreview, setFilePreview] = useState(null); // { hintUrl, hintType } of the newly picked file

  useEffect(() => {
    if (!file) {
      setFilePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreview({ hintUrl: url, hintType: mediaType(file.name) });
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // csv modal
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const warnedRef = useRef(false);

  const load = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const json = await getAdminQuestions({ token: adminToken, themeName: currentTheme });
      setQuestions(json.questions || []);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken]);

  useEffect(() => {
    if (currentTheme && !data) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme, data]);

  useEffect(() => {
    load();
  }, [load]);

  // the game cannot ask for more questions than exist (same warning as the PHP editor)
  useEffect(() => {
    if (loading || !data || warnedRef.current) return;
    const wanted = parseInt(data.noOfQuestion, 10) || 0;
    if (wanted > questions.length) {
      warnedRef.current = true;
      Swal.fire(
        "",
        `The number of questions to display (${wanted}) is more than the questions available (${questions.length}). Please add questions or update the setting.`,
        "warning"
      );
    }
  }, [loading, data, questions.length]);

  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visible = questions.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = "";
    setFile(null);
  };

  const onPickFile = (e) => {
    const f = e.target.files[0] || null;
    if (!f) {
      setFile(null);
      return;
    }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      Swal.fire("Invalid file", "Allowed formats: JPG, JPEG, PNG, GIF (image), MP4 (video), MP3 (audio).", "error");
      resetFileInput();
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      Swal.fire("File too large", `Maximum file size is ${MAX_FILE_MB} MB.`, "error");
      resetFileInput();
      return;
    }
    setFile(f);
  };

  const openAdd = () => {
    setModal({ mode: "add" });
    setQuestion("");
    setScores(["", "", ""]);
    setFile(null);
    setRemoveHint(false);
  };

  const openEdit = (q) => {
    setModal({ mode: "edit", id: q.id, current: q });
    setQuestion(he.decode(q.question_name));
    setScores([q.option_point_one, q.option_point_two, q.option_point_three].map((p) => cleanScore(p)));
    setFile(null);
    setRemoveHint(false);
  };

  const closeModal = () => {
    setModal(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const q = question.trim();

    let problem = "";
    if (!q) problem = "The question field cannot be empty.";
    else if (q.length > MAX_QUESTION_LEN) problem = `Question must be ${MAX_QUESTION_LEN} characters or less!`;
    else if (scores.some((s) => !String(s).trim())) problem = "All score fields must be filled.";
    else if (scores.some((s) => !/^\d+$/.test(s) || parseInt(s, 10) < 1 || parseInt(s, 10) > MAX_SCORE)) {
      problem = `Scores must be whole numbers from 1 to ${MAX_SCORE}.`;
    } else if (modal.mode === "add" && questions.length >= MAX_QUESTIONS) {
      problem = `A maximum of ${MAX_QUESTIONS} questions is allowed.`;
    }
    if (problem) {
      Swal.fire(problem, "", "error");
      return;
    }

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("action", modal.mode);
    formData.append("edit_id", modal.id || 0);
    formData.append("question_name", q);
    formData.append("option_point_one", scores[0]);
    formData.append("option_point_two", scores[1]);
    formData.append("option_point_three", scores[2]);
    if (file) formData.append("reference_file", file);
    else if (modal.mode === "edit" && removeHint) formData.append("removeHint", "true");

    setSaving(true);
    try {
      await saveQuestion({ token: adminToken, formData });
      closeModal();
      await Swal.fire({ title: "Updated..", icon: "success", timer: 1500, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire(err.message, "", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteQuestion({ token: adminToken, id: q.id });
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const downloadFile = (name, content, type) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadSampleCsv = () => {
    const headers = [
      "question_name",
      "option_one",
      "option_two",
      "option_three",
      "option_point_one",
      "option_point_two",
      "option_point_three",
    ];
    const sampleRows = [
      ["Do you feel included at work?", "Yes", "Maybe", "No", "10", "5", "1"],
      ["Would you speak up if you saw a biased comment?", "Yes", "Maybe", "No", "10", "5", "1"],
    ];
    const csvContent = [
      headers.join(","),
      ...sampleRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Sample_Inclusion_Quotient.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    try {
      const json = await exportQuestionsCSV({ token: adminToken, themeName: currentTheme });
      downloadFile("Export_Inclusion_Quotient.csv", json.csv, "text/csv;charset=utf-8");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      Swal.fire("Please choose a CSV file", "", "error");
      return;
    }
    const text = await csvFile.text();
    const rowCount = text.split(/\r\n|\r|\n/).filter((line) => line.trim() !== "").length - 1;
    if (rowCount > MAX_QUESTIONS) {
      Swal.fire("Too many questions", `A maximum of ${MAX_QUESTIONS} questions is allowed (CSV has ${rowCount}).`, "error");
      return;
    }
    const proceed = await Swal.fire({
      title: "Replace all questions?",
      text: "Uploading a CSV replaces every question of this theme.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, upload",
    });
    if (!proceed.isConfirmed) return;

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("file", csvFile);
    setCsvBusy(true);
    try {
      const json = await importQuestionsCSV({ token: adminToken, formData });
      setCsvOpen(false);
      setCsvFile(null);
      await Swal.fire("Successfully Updated", `Imported ${json.imported} questions`, "success");
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setCsvBusy(false);
    }
  };

  if (!currentTheme) {
    return (
      <div className="qe-empty">
        <p>No theme selected.</p>
        <button type="button" className="qe-btn" onClick={() => navigate("/admin")}>
          Go to themes
        </button>
      </div>
    );
  }

  const editing = modal && modal.mode === "edit" ? modal.current : null;
  const showCurrentMedia = editing && editing.hintUrl && !removeHint && !file;

  return (
    <div className="qe-page">
      <div className="qe-toolbar">
        <button type="button" className="qe-btn qe-back" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <div className="qe-actions">
          <button type="button" className="qe-btn" onClick={openAdd}>
            <i className="fa-solid fa-plus" /> Add Question
          </button>
          <button type="button" className="qe-btn" onClick={() => setCsvOpen(true)}>
            <i className="fa-solid fa-upload" /> Upload CSV
          </button>
          <button type="button" className="qe-btn" onClick={handleDownload}>
            <i className="fa-solid fa-download" /> Export CSV
          </button>
          <button type="button" className="qe-btn" onClick={downloadSampleCsv}>
            <i className="fa-solid fa-file-csv" /> Sample CSV
          </button>
        </div>
      </div>

      <div className="qe-card">
        <table className="qe-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Question Name</th>
              <th>Scores (Yes / Maybe / No)</th>
              <th>Hint</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6}>Loading...</td>
              </tr>
            )}
            {!loading && questions.length === 0 && (
              <tr>
                <td colSpan={6}>No questions yet. Add a question or upload a CSV.</td>
              </tr>
            )}
            {!loading &&
              visible.map((q, i) => (
                <tr key={q.id}>
                  <td>{pageStart + i + 1}</td>
                  <td className="qe-left">{he.decode(q.question_name)}</td>
                  <td className="qe-scores">
                    {q.option_point_one} / {q.option_point_two} / {q.option_point_three}
                  </td>
                  <td>
                    <Media q={q} />
                  </td>
                  <td>
                    <button type="button" className="qe-btn qe-small" onClick={() => openEdit(q)}>
                      <i className="fa-solid fa-pen-to-square" /> Edit
                    </button>
                  </td>
                  <td>
                    <button type="button" className="qe-btn qe-small qe-danger" onClick={() => handleDelete(q)}>
                      <i className="fa-solid fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {questions.length > PAGE_SIZE && (
        <div className="qe-pagination">
          <button type="button" className="qe-pgbtn" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            Prev
          </button>
          {pageItems(currentPage, totalPages).map((it, i) =>
            it === "..." ? (
              <span key={`gap${i}`} className="qe-gap">
                ...
              </span>
            ) : (
              <button
                key={it}
                type="button"
                className={`qe-pgbtn${it === currentPage ? " active" : ""}`}
                onClick={() => setPage(it)}
              >
                {it}
              </button>
            )
          )}
          <button
            type="button"
            className="qe-pgbtn"
            disabled={currentPage === totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {modal && (
        <div className="qe-backdrop" onClick={closeModal}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave}>
            <h4>{modal.mode === "edit" ? "Edit Question" : "Add Question"}</h4>

            <label htmlFor="qe-question">Question</label>
            <textarea
              id="qe-question"
              maxLength={MAX_QUESTION_LEN}
              placeholder="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <div className="qe-note qe-required">
              * Question is required. Max length {MAX_QUESTION_LEN} characters ({question.length} used)
            </div>

            <div className="qe-options">
              {OPTIONS.map((label, i) => (
                <div className="qe-option" key={label}>
                  <input type="text" value={label} readOnly aria-label={`Option ${i + 1}`} />
                  <input
                    type="number"
                    min="1"
                    max={MAX_SCORE}
                    step="1"
                    placeholder={`Score for Option ${["one", "two", "three"][i]}`}
                    value={scores[i]}
                    onKeyDown={(e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault()}
                    onChange={(e) => {
                      const next = cleanScore(e.target.value);
                      if (next !== "" && parseInt(next, 10) > MAX_SCORE) return; // max score is 100
                      setScores((s) => s.map((v, idx) => (idx === i ? next : v)));
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="qe-note qe-required">
              * Score is required for Yes, Maybe and No. Enter a whole number from 1 to {MAX_SCORE}.
            </div>

            <label htmlFor="qe-file">Upload Reference File:</label>
            {showCurrentMedia && (
              <div className="qe-current-media">
                <Media q={editing} />
                <button type="button" className="qe-btn qe-small qe-danger" onClick={() => setRemoveHint(true)}>
                  Remove
                </button>
              </div>
            )}
            {filePreview && (
              <div className="qe-current-media">
                <Media key={filePreview.hintUrl} q={filePreview} />
                <button type="button" className="qe-btn qe-small qe-danger" onClick={resetFileInput}>
                  Remove
                </button>
              </div>
            )}
            <input id="qe-file" ref={fileRef} type="file" accept={MEDIA_ACCEPT} onChange={onPickFile} />
            <div className="qe-note qe-required">
              * Allowed Format: Image (JPG, JPEG, PNG, GIF), Video (MP4), Audio (MP3)
            </div>
            <div className="qe-note qe-required">* Max file size {MAX_FILE_MB} MB</div>

            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="qe-btn" disabled={saving}>
                {saving ? "Saving..." : modal.mode === "edit" ? "Update" : "Add Question"}
              </button>
            </div>
          </form>
        </div>
      )}

      {csvOpen && (
        <div className="qe-backdrop" onClick={() => setCsvOpen(false)}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleImport}>
            <h4>Upload CSV</h4>
            <p className="qe-note">
              Columns: <code>question_name</code>, <code>option_one</code> (Yes), <code>option_two</code> (Maybe),{" "}
              <code>option_three</code> (No), <code>option_point_one</code>, <code>option_point_two</code>,{" "}
              <code>option_point_three</code> (whole numbers from 1 to {MAX_SCORE}, cannot be empty). Existing questions of this theme are
              replaced.
            </p>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0] || null)} />
            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={() => setCsvOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="qe-btn" disabled={csvBusy}>
                {csvBusy ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
